import type { ICustomTheme } from '@engyalo/delivery-ui-components/lib/interfaces/theme'
import type {
  ItemCardContentProps,
  ItemCardProps,
} from '@engyalo/delivery-ui-components/lib/ui/components/ItemCard'
import type { CartListProps } from '@engyalo/delivery-ui-components/lib/ui/components/CartList'
import type { ItemListProps } from '@engyalo/delivery-ui-components/lib/ui/components/ItemList'
import type { SnackbarProps } from '@engyalo/delivery-ui-components/lib/ui/components/Snackbar/Snackbar.types'
import type { SearchBarProps } from '@engyalo/delivery-ui-components'
import type { PriceProps } from '@engyalo/delivery-ui-components/lib/ui/components/Price'
import type { SessionDataArgs } from '../graphQL/queries/getSession/types'
import type { ICinnamonActions } from './actions'
import type DEFAULT_COMPONENTS_PROPS from '../consts/defaultConfigValues/defaultProps'
import type { IPackageProduct, IPlaceOrderFlow, UnresolvedRecord } from '.'

export interface IRoutes {
  route: string
  container: string
  exact?: boolean
}

export interface IComponentActionsProps {
  propName: string
  name: string
  params: UnresolvedRecord
}

export interface Icomponent {
  uid: string
  name: string
  importRoute: string
  defaultProps?: keyof typeof DEFAULT_COMPONENTS_PROPS
  props?: UnresolvedRecord & {
    actions?: Array<IComponentActionsProps>
    runOnLoad?: ICinnamonActions
    hideOnScroll?: boolean
  }
  isExternal?: boolean
}

export type IProcessedComponentProps<T> = UnresolvedRecord &
  DefaultProps<T> & {
    runOnLoad?: (...args: unknown[]) => void
    reachBottomActions?: (...args: unknown[]) => void
  }
export interface IProcessedComponent<T = unknown> {
  uid: string
  name: string
  importRoute: string
  defaultProps?: keyof typeof DEFAULT_COMPONENTS_PROPS
  props?: IProcessedComponentProps<T>
}

export interface IContainerSections {
  components: Array<Icomponent>
  props?: UnresolvedRecord
}

export interface IProcessedContainerSections<T = unknown, U = unknown> {
  components: Array<IProcessedComponent<T>>
  props?: IProcessedComponentProps<U>
}

export interface IContainersBase {
  uid: string
  type?:
    | 'cart'
    | 'catalog'
    | 'catalogFixed'
    | 'categories'
    | 'filters'
    | 'summary'
    | 'categoriesnav'
    | 'productdetail'
    | 'orderHistory'
    | 'orderDetail'
    | 'orderConfirmation'
  title: string
  slug: string
  // @Todo: Fixme! We should know the type for this
  header?: IContainerSections
  body: IContainerSections
  footer?: IContainerSections
  skipDefaults?: boolean
}

export interface IModal extends IContainersBase {
  props?: UnresolvedRecord
}

export interface IModalsObject extends Record<string, IModal> {}

export interface IContainers extends IContainersBase {
  modals?: Array<string>
}

export interface IProcessedContainer
  extends Pick<IContainers, 'uid' | 'modals'> {
  header: IProcessedContainerSections
  body: IProcessedContainerSections
  footer: IProcessedContainerSections
}

export interface IContainersObject extends Record<string, IContainers> {}

/**
 * One group of texts, e.g. `texts.cart` or `texts.orders`.
 *
 * A leaf is a string, **or one more level of strings**. The nesting is not
 * decorative: `texts.orders.detail.refreshLabel`, `texts.orders.status.CREATED`
 * and `texts.orderConfirmation.failed.title` are all real tokens, and
 * `processData` resolves a dotted path by walking objects
 * (`selectors.reduce((src, key) => src?.[key])`), so a flat `"detail.x"` key
 * would not resolve.
 *
 * The previous declaration allowed two levels only. It was already inaccurate
 * — those `orders` tokens have existed since the Order Detail screen shipped —
 * and nothing caught it because no `defaultTexts` file carried an `orders` key,
 * so the shape never reached a type-checked file.
 *
 * Consequence for readers: a two-level access is now `string | ITextGroup`
 * rather than `string`. Call sites that only read flat keys go through
 * `flatTexts` rather than each carrying its own cast.
 */
export interface ITextGroup {
  [key: string]: string | { [key: string]: string }
}

export interface ITextLibrary {
  [key: string]: ITextGroup
}

export interface IMetatags {
  description?: string
  keywords?: string
  author?: string
  favicon?: string
  title?: string
  charset?: string
  lang?: string
}
export interface ITemplateLibrary {
  list: Record<string, string>
  components: {
    [name: string]: {
      [variant: string]: {
        [prop: string]: any
      }
    }
  }
}

export enum ErrorMessageTranslation {
  GetCart = 1,
  CatalogueEmptyData,
  InitialData,
  NonProvidedParams,
  ErrorOnInitialLoad,
}

export interface IAnalytics {
  googleAnalyticsId: string
  fbPixelId: string
}

/**
 * Package field the multipackage rows may be ordered by.
 *
 * A deliberate subset of `IPackageProduct`, written with `Pick` so that
 * renaming or removing one of these package fields fails to compile here, at
 * the declaration, rather than at the call site.
 *
 * Excluded on purpose:
 * - `stock` is live, per-customer data. Ordering by it would flip as stock
 *   depletes and differ between two shoppers looking at the same product, which
 *   is the very inconsistency this option exists to remove.
 * - `maxQty`, `minQty` and `jumpQty` are nullable, and `null` coerces to `0` in
 *   a `<` / `>` comparison, so a package with no limit would lead the ascending
 *   order as though its limit were zero.
 * - `suffix` is a formatted price string, and `value` is the shopper's own cart
 *   quantity; neither describes the package.
 */
export type PackageSortField = keyof Pick<
  IPackageProduct,
  'quantity' | 'price' | 'label' | 'name'
>

/**
 * Renders the multipackage rows in a fixed order instead of the order the
 * catalog returns them in.
 *
 * The catalog order is whatever each product document happens to store, so it
 * varies from product to product: some list the unit package first, others the
 * box. Customers add quantity to whichever row is on top, so an inconsistent
 * order makes them buy boxes when they meant units.
 *
 * Off by default; packages keep catalog order unless a storefront opts in.
 *
 * These values arrive as remote JSON, so the types below are authoring
 * guidance rather than a runtime guard. Anything unrecognised — a misspelled
 * `field`, a `direction` that is neither `asc` nor `desc`, an `enabled` that
 * isn't a boolean — leaves the packages in catalog order and reports once when
 * the config loads, rather than ordering by a value nobody chose. `direction`
 * is matched case-insensitively; `field` is not.
 *
 * Applied in `sanitizeExtraPackages` through the shared `sortByKey` helper,
 * whose sort is stable, so packages with an equal key keep their catalog order.
 */
export type PackageSortConfig = {
  /**
   * Opt-in, and strictly the boolean `true`. Absent, `false`, or any
   * non-boolean leaves the catalog order untouched.
   */
  enabled?: boolean
  /**
   * Defaults to `quantity` — units per package, which is what places the single
   * unit above the box.
   *
   * `label` and `name` compare by UTF-16 code unit rather than by locale, so
   * case dominates: every capitalised label sorts ahead of every lowercase one
   * ("Unidad" before "caja chica"). A non-ASCII character sorts after every
   * ASCII letter, but only when it falls at the first differing position —
   * "Cajón" still sorts next to "Caja", not last.
   */
  field?: PackageSortField
  /** Defaults to `asc`. Matched case-insensitively. */
  direction?: 'asc' | 'desc'
}

export interface IGlobals {
  searchBar?: {
    searchTermMinLength?: SearchBarProps['searchTermMinLength']
    searchTermsExceptions?: SearchBarProps['searchTermsExceptions']
    searchTimeout?: SearchBarProps['searchTimeout']
  }
  navMenu?: 'tabs' | 'dropdown'
  counter?: {
    step?: number
    activeInput?: boolean
    max?: number | null
    modifierMap?: Record<string, number>
    allowRetryWhenOutOfStock?: boolean
    clampTypedInputToCap?: boolean
    allowTypedZero?: boolean
    /**
     * Treats a field the customer emptied as a typed zero, so the gesture that
     * ends the edit removes the line.
     *
     * **Requires `allowTypedZero`, and the library enforces that** rather than
     * merely documenting it: with that flag off this one holds nothing and
     * commits nothing, so enabling it alone is inert.
     *
     * Exists because `allowTypedZero` alone is unreachable on the platform this
     * webview mostly runs on. The counter renders `<input type="number">`
     * outside a `<form>`, which on Android raises a numeric keypad whose action
     * key moves focus and dispatches no keyboard event at all — so before
     * `@engyalo/delivery-ui-components@1.21.2` the only commit gesture was an
     * Enter key that does not exist there, and a customer who cleared the field
     * was left with a blank box over a quantity still being charged. 1.21.2
     * makes blur a commit gesture too, exempting the blurs that are not a
     * decision (a pointer gesture that began inside the counter, and the window
     * losing focus).
     *
     * ⚠️ **Destructive, with no confirmation of its own.** Clearing a quantity
     * and tapping away drops the line. Bound on `CounterDefault`, so it reaches
     * all eight counter variants; a storefront that wants it in only some
     * places overrides the prop per variant in its SCS config rather than
     * setting this global.
     *
     * Default `false`, per the zero-surprises invariant `allowTypedZero`
     * follows — no storefront changes behaviour until it authors this key.
     */
    allowEmptyInputAsZero?: boolean
    /**
     * Disables the product detail Counter while its add-to-cart mutation is in
     * flight, so the displayed quantity resyncs to the real one when the server
     * answers. The resync fires on the `true -> false` edge, i.e. when the
     * mutation settles, which is what makes a rejected increment fall back to
     * the allowed quantity instead of showing what the user typed.
     *
     * Leave this off unless the installed `@engyalo/delivery-ui-components`
     * actually ignores `+` / `-` clicks while the Counter is disabled. In
     * versions that only dim it, the click still goes through: the second tap
     * aborts the in-flight request, the abort settles the flag, and the resync
     * snaps the display back to the last confirmed quantity mid-edit. That is
     * worse than the bug this fixes, and it also defeats cinnamon's own
     * abort-and-resend design, which exists so a user can keep incrementing
     * during a round trip.
     */
    disableWhileSyncing?: boolean
  }
  packages?: {
    sort?: PackageSortConfig
  }
  catalog?: {
    layout?: ItemListProps['layout']
    bodyFields?: ItemCardContentProps['fields']
    footerFields?: ItemCardContentProps['fields']
    fieldsToDisplay?: ItemCardContentProps['fieldsToDisplay']
    /**
     * Let the catalogue lists skip re-rendering while none of their items and
     * none of their other props have changed by value.
     *
     * Cinnamon resolves component props from configuration on every state
     * change, so each render hands the list new objects and it re-renders even
     * when no product moved. Comparing by value instead removes almost all of
     * those renders; on a large catalogue it kept the cost of loading another
     * page flat with depth instead of growing with it.
     *
     * Off by default so existing storefronts keep their current render timing.
     *
     * Requires a `delivery-ui-components` whose `ItemList` consumes the prop.
     * On an older version it is **not** ignored: it falls through `ItemList`'s
     * rest props and nothing in the chain below them stops it, so it reaches
     * the DOM as an unknown attribute and React warns about it once per
     * catalogue list. The chain is spelled out where the binding lives
     * (`defaultProps/CatalogueList.ts`). Do not enable this, or merge the
     * binding, before the dependency is bumped.
     *
     * @default false
     */
    memoizeItems?: boolean
  }
  price?: {
    packagePriceMap?: Record<string, string>
  } & Partial<PriceProps>
  unitPrice?: {
    showUnitPrice?: boolean
    unitsSize?: number | string
    unitsColor?: string
    unitsInline?: boolean
  }
  minAmount?: {
    showAmount?: boolean
    showIndicator?: boolean
  }
  tags?: ItemCardProps['tags']
  sideContent?: ItemCardProps['sideContent']
  cartLabels?: CartListProps['labelMap']
  imageConfig?: {
    resizeEnabled: boolean
    imageSizes: {
      cartImage: string
      catalogueImage: string
      suggestedItemImage: string
      productDetailImage: string
      searchImage: string
      filterImage: string
      frequentlyBoughtTogetherImage: string
    }
  }
  /**
   * How many products each recommendation placement asks the API for.
   *
   * One block for all four rather than two adjacent look-alike keys: a
   * storefront author told to "raise the recommendations limit" should not
   * have to know which carousel is called what, and guessing wrong would
   * merge cleanly into a real global that tunes a different placement.
   *
   * Every one of these is sent as the GraphQL `$limit: Int`. `carouselLimit`
   * and `listLimit` are normalised at config load — a value that is not a
   * positive integer is replaced by the shipped default and reported to
   * Datadog. The two legacy keys are deliberately left unnormalised, since
   * changing what they accept would change what existing storefronts render.
   */
  recommendedProducts: {
    /** Legacy suggested-items "ver todos" list. */
    allRecommendations: number
    /** Legacy suggested-items carousel. */
    widgetHome: number
    /** Buy Again, Previously Purchased and Recommended For You carousels. */
    carouselLimit: number
    /** The full-page lists behind those carousels' header button. */
    listLimit: number
  }
  prefillCart: {
    createOrder: boolean
    limit: number
  }
  placeOrderFlow: Partial<IPlaceOrderFlow>
  [otherProps: string]: any
}

export enum Localization {
  En = 'en',
  Es = 'es',
  Pt = 'pt',
  Zh = 'zh',
  Ar = 'ar',
  He = 'he',
  Th = 'th',
  EnUs = 'en-us',
  EsMx = 'es-mx',
  PtBr = 'pt-br',
  ZhCn = 'zh-cn',
}

/**
 * Hides all monetary values for customers whose session custom field matches.
 * `customField` is a dot-path into `customer.customFields` (e.g. `flags.hide`);
 * `flagValue` is compared via string coercion (so `true`/`1` match `"true"`/`"1"`).
 * All three fields are required to hide anything — `enabled: true` alone does
 * nothing, and there is no unconditional hide-all.
 */
export type HidePricesConfig = {
  enabled?: boolean
  customField?: string
  flagValue?: string
}

/**
 * Overwrites the backend min-order message with a {{percentage}} progress text.
 * Opt-in via `enabled` (default false); `audience` defaults to 'all' (every
 * shopper), or 'hidePrices' to gate it to the hide-prices cohort. `message` is
 * an optional inline override (else falls back to texts.cart.minAmountProgress).
 */
export type OverwriteMinAmountMessageConfig = {
  enabled?: boolean
  audience?: 'all' | 'hidePrices'
  message?: string
}

export interface IConfig {
  routes: IRoutes[]
  defaultRoute?: string
  phoneNumber: string
  containers: IContainersObject
  logo?: string
  modals?: IModalsObject
  theme: ICustomTheme
  name: string
  botSlug: string
  globalErrorMessage: string
  texts: ITextLibrary
  metatags?: IMetatags
  templates: ITemplateLibrary
  globals: IGlobals
  analytics?: IAnalytics
  options?: {
    localization?: Localization
    systemThemeMode?: boolean
    orderMinAmount?: number
    overwriteMinAmountMessage?: OverwriteMinAmountMessageConfig
    currency?: string
    currencySymbol?: string
    currencyCode?: string
    currencyPrefix?: string
    pagination?: boolean
    useModal?: boolean
    format?: string
    promotionPercentageFormat?: string
    currencyDisplay?: string
    hidePrices?: HidePricesConfig
    /** derived — set by the config slice, do not author */
    hidePricesActive?: boolean
    /**
     * Whether "Order again" replaces the cart instead of appending to it.
     *
     * Defaults to appending. Getting the default wrong the other way would
     * silently discard a cart the shopper was still building, so replacing is
     * something a storefront opts into.
     */
    reorderReplacesCart?: boolean
    /**
     * Whether the webview creates and confirms the order itself, instead of
     * handing the shopper back to the conversation for the flow to do it.
     *
     * **The default is today's behaviour, and that is the point of the
     * option.** Every storefront in the estate reaches checkout through
     * `useCreateOrderHook` → `postCreateOrder`, which POSTs the stephook and
     * then redirects to the channel; the flow creates the order afterwards, in
     * chat. Opting in moves creation into the webview
     * (`hooks/orders/useOrderConfirmation.ts`) and suppresses that redirect,
     * so a storefront that has not authored this must behave byte-identically
     * and must never call `createOrder`.
     *
     * Compared against literal `true` for the reason
     * `orderConfirmation.required` is: the config is remote JSON with no
     * boolean guarantee, and a quoted `"false"` is truthy.
     *
     * ⚠️ Authoring this **without** editing the storefront's Builder flow
     * breaks checkout for the shopper, per storefront. The usual symptom is
     * *not* two orders: while the webview's order is still `CREATED`,
     * `validateOrderCreated` rejects the flow's preorder step with a `409
     * ALREADY_EXIST`, which surfaces as an error in the conversation with the
     * cart already consumed. Genuine double creation needs the webview's order
     * to have left `CREATED` first — confirmed, cancelled or errored — so plan
     * for a broken flow, not for reconciling duplicates.
     *
     * ⚠️ Unusable on a storefront with an active order splitter
     * (`configuration.splitters.order`). `createOrder` and `confirmOrder` both
     * reject outright there, naming `createOrders`/`confirmOrders`, which
     * cinnamon does not implement. `useCreateOrder` detects the combination,
     * declines the opt-in, takes the legacy path and reports to Datadog — so
     * authoring it is inert rather than harmful, but it will not do what the
     * author intended.
     *
     * What confirmation then applies is not decided here: it is derived from
     * the status `createOrder` returns, with the parameters on
     * `configuration.order.confirmation` (a *session* config block, authored
     * separately). This option only decides who creates the order.
     */
    inWebviewOrderConfirmation?: boolean
    placeholderImage?: string
    usePaginatedCategories?: boolean
    /**
     * Use total in cart groups, if false show subtotal
     */
    cartGroupingTotals?: boolean
    /**
     * Show freegoods as subitem in cart
     */
    freegoodsAsSubitem?: boolean
    /**
     * Show freegods as group in cart
     */
    freegoodAsCartGroup?: boolean
    /**
     * Show freegoods combo promo origin in cart
     */
    showFreegoodsComboOrigin?: boolean
    /**
     * Render a fully-gifted TOTAL promotion line at its real per-unit price
     * (`price - discounts / quantity`, i.e. 0 for a gift) instead of the value
     * derived from `discountsNoCombo`, which nets to the full unit price when
     * every unit is free.
     *
     * Only applies when the whole line is gifted (`quantity === promotionalQty`);
     * a line carrying paid units of the same SKU is unaffected. The promotion's
     * `pricePerItem` is deliberately not used -- backends disagree on its unit,
     * returning a per-unit price on one storefront and the whole line's value on
     * another.
     *
     * Note this sets the unit *price*. Whether the line *total* also shows as
     * "$0.00" is up to the storefront's ItemCard config -- `cartCondensed` sets
     * `priceTotal.hideOnZero: true`, which hides a zero total unless the
     * storefront overrides it.
     */
    showTotalFreegoodZeroPrice?: boolean
    /**
     * Ignore subtotal on cart
     */
    ignoreSubtotal?: boolean
    /**
     * Allow non zero subtotal orders
     */
    allowNonZeroSubtotalOrders?: boolean
    /**
     * Show restrictions apply label on cart
     */
    hideCartRestrictions?: boolean
    splash?: {
      logo?: boolean | string
      label?: string
      hideIcon?: boolean
    }
    /**
     * Hide subcategories options
     */
    hideSubcategories?: boolean
    /**
     * Hide products on subcategories
     */
    hideSubCategoriesProducts?: boolean
    /**
     * shows image for subcategories list
     */
    showSubCategoriesImages?: boolean
    /**
     * Hide products on subcategories
     */
    productDetailView?: boolean
    /**
     * track eventsURL, works as feature flag and also as parameter for send events
     */
    trackEventsURL?: string | null
    /**
     * Pagination size for the catalog
     */
    paginationSize?: number
    /**
     * url for resizer service
     */
    resizeServiceUrl?: string
    /**
     * Force show hidden categories
     * @default false
     */
    showHiddenCategories?: boolean
    /**
     * fill the qty for search result based on the cart item
     */
    fillSearchQty?: boolean
    /**
     * Text direction for UI elements
     */
    textDir?: React.HTMLAttributes<HTMLDivElement>['dir']
    /**
     * Split promotion tag in catalog
     */
    splitPromotionTagCatalog?: boolean
    /**
     * Split promotion tag in recommended products
     */
    splitPromotionTagRecommendedProducts?: boolean
  }
}

export interface IModals
  extends Record<
    string,
    {
      show: boolean
      state?: UnresolvedRecord
      item?: string
    }
  > {}

export interface IDialog {
  show: boolean
  id?: string
  icon: {
    name: string
  }
  title:
    | string
    | {
        value: string
      }
  message: {
    value: string
  }
  buttons: any
}

export enum CheckoutRules {
  CART_CONDITION_PENDING_MAX_QTY = 'cartConditionPendingMaxQty',
  CART_CONDITION_PENDING_MIN_QTY = 'cartConditionPendingMinQty',
  CART_CONDITION_PENDING_MAX_AMOUNT = 'cartConditionPendingMaxAmount',
  CART_CONDITION_PENDING_MIN_AMOUNT = 'cartConditionPendingMinAmount',
  CART_CONDITION_PENDING_USER_VALIDATION = 'cartConditionPendingUserValidation',
  CART_CONDITION_PENDING_MIN_ADDITIONAL_MEASUREMENT = 'cartConditionPendingMinAdditionalMeasurement',
  CART_CONDITION_PENDING_MAX_ADDITIONAL_MEASUREMENT = 'cartConditionPendingMaxAdditionalMeasurement',
  CART_WARNING_CHECK_RETURNABLES = 'cartWarningCheckReturnables',
  ORDER_DAILY_LIMIT = 'orderDailyLimit',
  CART_CONDITION_CHECK_LINKED_PRODUCTS = 'cartConditionCheckLinkedProducts',
}

export enum AlertConstrains {
  cartConditionPendingMinQty = 'cartConditionPendingMinQty',
  cartConditionPendingMinAmount = 'cartConditionPendingMinAmount',
  cartConditionPendingMaxAmount = 'cartConditionPendingMaxAmount',
  cartConditionPendingMaxQty = 'cartConditionPendingMaxQty',
  creditLimitMaxAmount = 'creditLimitMaxAmount',
}
export type IAlertsStatus = Record<AlertConstrains, { isActive: boolean }>

export interface IAlert
  extends Pick<
    SnackbarProps,
    | 'open'
    | 'message'
    | 'title'
    | 'origin'
    | 'topOffset'
    | 'bottomOffset'
    | 'showIcon'
    | 'showCloseButton'
    | 'closeOnClick'
    | 'autoHideDuration'
    | 'severity'
    | 'variant'
    | 'color'
    | 'size'
    | 'align'
  > {
  key?: string
}

export type LoaderConfig = string | { name: string; delay: number }
export interface IParams
  extends Record<string, string | UnresolvedRecord | undefined> {
  location?: {
    pathname: string
  }
}

export enum SessionNotifications {
  STOCK_AVAILABLE = 'STOCK_AVAILABLE',
  AUTHENTICATION_NEEDED = 'AUTHENTICATION_NEEDED',
  SESSION_CREATED = 'SESSION_CREATED',
  ORDER_REMINDER = 'ORDER_REMINDER',
  ABANDONED_CART = 'ABANDONED_CART',
}

export type SessionNotificationsArray = Array<{
  type: SessionNotifications
  enabled: boolean
}>

export interface ISliceDefault {
  loaders: string[]
  loading: boolean
  loadingDelay: number
  config?: IConfig
  sessionId?: string
  storeName?: string
  modals?: IModals
  dialogs?: IDialog
  sessionData?: SessionDataArgs
  validators: any[]
  errorMessage?: string | null
  currentModalItem?: string
  globalError: boolean
  showErrorTitle: boolean
  excludedUiComponents: string[]
  cartAlerts: IAlertsStatus
  params: IParams
  secondaryLoader?: boolean
  showRecommendations: boolean
  showSuggestedItems: boolean
  alerts: IAlert[]
  onErrorCallback?: () => void
  sessionNotifications?: Record<SessionNotifications, boolean>
}

export enum LoadersType {
  INITIAL_DATA = 'INITIAL_DATA',
  STORE_CONFIG = 'STORE_CONFIG',
  CREATE_ORDER = 'CREATE_ORDER',
}

export type DefaultProps<T> = Partial<T> & {
  template?: string
  runOnLoad?: ICinnamonActions
  hideOnScroll?: boolean
  hideOnScrollOffset?: number
  reachBottomActions?: ICinnamonActions
  [prop: string]: any
}

export interface IGetConfigArgs {
  slug: string
  language?: Localization
}
